import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import CustomPopover, { CustomPopoverProps } from '@components/CustomPopover'
import {
  SHORTLIST_FILTERS,
  SHORTLIST_FILTER_OPERATORS_BY_CUSTOM_FIELD,
  SHORTLIST_FILTER_OPERATORS_BY_FIELD,
  SHORTLIST_SEARCH_DEBOUNCE_TIME,
} from '@modules/shortlist/constants'
import {
  CustomColumnType,
  FilterCombinator,
  FilterOperator,
  Shortlist,
  ShortlistColumnFilter,
  ShortlistFilterField,
} from '@modules/shortlist/types'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import { FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue'
import { colors } from '@theme/variables'

interface ShortlistFilterPopoverProps {
  anchor: CustomPopoverProps['anchor']
  activeShortlist?: Shortlist
  existingFilters?: ShortlistColumnFilter[]
  handleClose: (value: Nullable<HTMLElement>) => void
  filtersUpdated: (filters: ShortlistColumnFilter[]) => void
}

const DEFAULT_FILTER = {
  attribute: SHORTLIST_FILTERS[0].attribute,
  type: SHORTLIST_FILTERS[0].attribute,
  operator: FilterOperator.CONTAINS,
  combinator: '',
}

const ShortlistFilterPopover = ({
  anchor,
  activeShortlist,
  existingFilters,
  handleClose,
  filtersUpdated,
}: ShortlistFilterPopoverProps) => {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<ShortlistColumnFilter[]>(existingFilters || [DEFAULT_FILTER])
  const debouncedFilters = useDebouncedValue(filters, SHORTLIST_SEARCH_DEBOUNCE_TIME)

  const filterColumns = useMemo(
    () =>
      SHORTLIST_FILTERS.reduce((acc, field) => {
        if (field.attribute !== ShortlistFilterField.customFieldValues) {
          acc.push({
            ...field,
            type: field.attribute,
          })
        } else {
          acc.push(
            ...(activeShortlist?.customFields?.map(customField => ({
              attribute: customField.id!,
              label: customField.fieldName!,
              type: ShortlistFilterField.customFieldValues,
            })) || []),
          )
        }

        return acc
      }, [] as { attribute: string | number; type: ShortlistFilterField; label: string }[]),
    [activeShortlist],
  )

  useEffect(() => {
    if (debouncedFilters === existingFilters) return
    filtersUpdated(
      filters
        .filter(
          filter =>
            !!filter.value || filter.operator === FilterOperator.IS_EMPTY || filter.operator === FilterOperator.IS_NOT_EMPTY,
        )
        .map(filter => ({
          ...filter,
          nestedAttribute: filter.type === ShortlistFilterField.customFieldValues ? (filter.attribute as number) : '',
        })),
    )
  }, [debouncedFilters])

  const handleAddNewFilter = useCallback(() => {
    setFilters([...filters, DEFAULT_FILTER])
  }, [filters, filterColumns])

  const handleFilterDelete = useCallback(
    (indexToDelete: number) => {
      setFilters([...filters.filter((_, index) => index !== indexToDelete)])
    },
    [filters, filterColumns],
  )

  const handleDeleteAll = useCallback(() => {
    setFilters([])
  }, [filters, filterColumns])

  const handleFilterUpdate = useCallback(
    (indexToUpdate: number, filter: ShortlistColumnFilter) => {
      const newFilters = [...filters]
      newFilters[indexToUpdate] = { ...filter }
      setFilters(newFilters)
    },
    [filters, filterColumns],
  )

  const shouldShowValueSelection = (filter: ShortlistColumnFilter) =>
    filter.operator !== FilterOperator.IS_EMPTY && filter.operator !== FilterOperator.IS_NOT_EMPTY

  const availableOperatorsByAttribute = useMemo(() => {
    if (!filterColumns) return {} as { [key: string]: FilterOperator[] }

    return filterColumns.reduce((accum, curr) => {
      let operators
      if (curr.type === ShortlistFilterField.customFieldValues) {
        const customField = activeShortlist?.customFields?.find(field => field.id === (curr.attribute as number))
        operators = SHORTLIST_FILTER_OPERATORS_BY_CUSTOM_FIELD[customField?.fieldType as CustomColumnType]
      } else {
        operators = SHORTLIST_FILTER_OPERATORS_BY_FIELD[curr.type]
      }
      return {
        ...accum,
        [curr.attribute.toString()]: operators,
      }
    }, {} as { [key: string]: FilterOperator[] })
  }, [filterColumns])

  const handleFilterFieldChange = useCallback(
    (indexToUpdate: number, attribute: ShortlistFilterField) => {
      const newFilters = [...filters]
      const filterColumn = filterColumns.find(filter => filter.attribute === attribute)

      const newFilter = {
        ...filters[indexToUpdate],
        attribute,
        type: filterColumn!.type,
      }

      if (
        filterColumn?.attribute &&
        !availableOperatorsByAttribute[filterColumn?.attribute.toString()]?.some(operator => operator === newFilter.operator)
      ) {
        const [operatorToSet] = availableOperatorsByAttribute[filterColumn.attribute.toString()]
        newFilter.operator = operatorToSet
      }

      newFilters[indexToUpdate] = newFilter
      setFilters(newFilters)
    },
    [filters, filterColumns, availableOperatorsByAttribute],
  )

  return (
    <CustomPopover id="filter_shortlist_profiles" anchor={anchor} setAnchor={handleClose}>
      <Box padding="16px" width={660} position="relative">
        <Box position="absolute" top="8px" right="8px">
          <IconButton onClick={() => handleClose(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box>
          <Box maxWidth="400px">
            <Typography variant="h2" alignContent="center">
              {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body1" alignContent="center">
              {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_POPOVER_TEXT)}
            </Typography>
          </Box>
          {filters.map((filter, index) => (
            <Box display="flex" justifyContent="center" alignItems="center" gap="20px">
              <Box display="flex" flex="1" alignItems="center">
                <IconButton sx={{ marginTop: '10px', height: 'fit-content' }} onClick={() => handleFilterDelete(index)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Box flex="1" visibility={index ? 'inherit' : 'hidden'}>
                  <FormControl style={{ minWidth: '80px' }} variant="outlined" margin="normal">
                    <InputLabel
                      sx={{ background: colors.white, padding: '0 6px', marginTop: '2px', color: '#00000099' }}
                      data-testid="shortlist-filter-combination-label"
                      shrink
                    >
                      {t(translationKeys.CUSTOM_LIST_FILTER_LOGIC_COMBINATION)}
                    </InputLabel>
                    <Select
                      data-testid="shortlist-filter-combination"
                      notched
                      value={filter.combinator}
                      labelId="shortlist-filter-combination"
                      onChange={(e: SelectChangeEvent<FilterCombinator | string>) =>
                        handleFilterUpdate(index, {
                          ...filter,
                          combinator: e.target.value as FilterCombinator,
                        })
                      }
                    >
                      <MenuItem value={FilterCombinator.AND}>{t(FilterCombinator.AND)}</MenuItem>
                      <MenuItem value={FilterCombinator.OR}>{t(FilterCombinator.OR)}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <FormControl variant="outlined" style={{ minWidth: '150px' }} margin="normal">
                <InputLabel
                  sx={{ background: colors.white, padding: '0 6px', marginTop: '2px', color: '#00000099' }}
                  data-testid="shortlist-filter-column"
                  shrink
                >
                  {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_COLUMN)}
                </InputLabel>
                <Select
                  date-testid="shortlist-filter-column"
                  notched
                  value={filter.attribute as string}
                  labelId="profile-filter-column"
                  onChange={(e: SelectChangeEvent<string>) =>
                    handleFilterFieldChange(index, e.target.value as ShortlistFilterField)
                  }
                >
                  {filterColumns.map(availableField => (
                    <MenuItem key={availableField.attribute} value={availableField.attribute}>
                      {t(availableField.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" style={{ minWidth: '150px' }} margin="normal">
                <InputLabel
                  sx={{ background: colors.white, padding: '0 6px', marginTop: '2px', color: '#00000099' }}
                  data-testid="shortlist-filter-operator"
                  shrink
                >
                  {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_OPERATOR)}
                </InputLabel>
                <Select
                  id="shortlist-filter-operator"
                  notched
                  value={filter.operator}
                  labelId="shortlist-filter-operator"
                  onChange={(e: SelectChangeEvent<FilterOperator>) =>
                    handleFilterUpdate(index, {
                      ...filter,
                      operator: e.target.value as FilterOperator,
                    })
                  }
                >
                  {availableOperatorsByAttribute[filter.attribute.toString()].map(option => (
                    <MenuItem value={option}>{t(option)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                style={{ minWidth: '150px', visibility: shouldShowValueSelection(filter) ? 'inherit' : 'hidden' }}
                margin="normal"
              >
                <InputLabel
                  sx={{ background: colors.white, padding: '0 6px', marginTop: '2px', color: '#00000099' }}
                  id="shortlist-filter-value"
                  shrink
                >
                  {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_VALUE)}
                </InputLabel>
                <TextField
                  data-testid="shortlist-filter-value"
                  value={filter.value}
                  onChange={e =>
                    handleFilterUpdate(index, {
                      ...filter,
                      value: e.target.value,
                    })
                  }
                />
              </FormControl>
            </Box>
          ))}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Button
              variant="text"
              color="primary"
              data-testid="add_new_filter_shortlist"
              startIcon={<AddIcon color="inherit" sx={{ marginLeft: '-10px', fontSize: '32px' }} />}
              onClick={handleAddNewFilter}
              sx={{ marginLeft: '5px' }}
            >
              {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_ADD_FILTER_BTN)}
            </Button>
            <Button
              variant="text"
              color="primary"
              data-testid="remove_all_filters_shortlist"
              startIcon={<DeleteIcon color="inherit" sx={{ marginLeft: '-10px', fontSize: '32px' }} />}
              onClick={handleDeleteAll}
              sx={{ marginRight: '-5px' }}
            >
              {t(translationKeys.CUSTOM_LIST_FILTER_SHORTLIST_REMOVE_ALL_BTN)}
            </Button>
          </Box>
        </Box>
      </Box>
    </CustomPopover>
  )
}

export default memo(ShortlistFilterPopover)
